import { Badge, MonetaryAmount } from '@root/components'
import { getDocumentsPageUrl } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { useVirtualList } from '@root/hooks'
import {
  AutomationRulePreviewTransaction,
  getImportSourceLabelKey,
  ImportDataSource
} from '@root/query'
import { formatDate } from '@root/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCheck,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaFileCsv,
  FaFileInvoice,
  FaFileInvoiceDollar,
  FaSpinner,
  FaUniversity,
  FaUserClock
} from 'react-icons/fa'
import styled, { DefaultTheme } from 'styled-components'

export type PreviewOutcome =
  | { kind: 'loading' }
  | { kind: 'matches'; count: number; results: AutomationRulePreviewTransaction[] }
  | { kind: 'failed' }

export interface AccountingRulePreviewResultsProps {
  outcome: PreviewOutcome
  isEdit?: boolean
  isPurchaseInvoiceRule?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
}

const ITEM_HEIGHT = 92

const sourceIcon = (source: string | null) => {
  switch (source) {
    case ImportDataSource.CSV:
      return <FaFileCsv />
    case ImportDataSource.SALES_INVOICE:
      return <FaFileInvoice />
    case ImportDataSource.PURCHASE_INVOICE:
      return <FaFileInvoiceDollar />
    case ImportDataSource.SALAXY:
      return <FaUserClock />
    default:
      return <FaUniversity />
  }
}

const AccountingRulePreviewResults: React.FC<AccountingRulePreviewResultsProps> = ({
  outcome,
  isEdit = false,
  isPurchaseInvoiceRule = false,
  hasNextPage = false,
  onLoadMore
}) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const results = outcome.kind === 'matches' ? outcome.results : []
  const hasList = outcome.kind === 'matches' && outcome.count > 0
  const { scrollRef, renderItems } = useVirtualList({
    items: results,
    itemSize: ITEM_HEIGHT,
    onScrollBottom: () => {
      if (hasNextPage) onLoadMore?.()
    }
  })

  const icon = {
    loading: <FaSpinner className="spin" />,
    matches: <FaCheck />,
    failed: <FaExclamationTriangle />
  }[outcome.kind]

  const description =
    outcome.kind === 'loading'
      ? t('settings.automation.accountingRules.form.previewLoading')
      : outcome.kind === 'failed'
      ? t('settings.automation.accountingRules.form.previewFailed')
      : t(
          isPurchaseInvoiceRule
            ? 'settings.automation.accountingRules.form.previewDescriptionPurchase'
            : 'settings.automation.accountingRules.form.previewDescription',
          { count: outcome.count }
        )

  return (
    <StyledPreviewResults $hasList={hasList}>
      <StyledPreviewIcon $variant={outcome.kind}>{icon}</StyledPreviewIcon>
      <StyledPreviewTitle>
        {t(
          isEdit
            ? 'settings.automation.accountingRules.form.previewTitleEdit'
            : 'settings.automation.accountingRules.form.previewTitle'
        )}
      </StyledPreviewTitle>
      <StyledPreviewDescription>{description}</StyledPreviewDescription>

      {outcome.kind === 'matches' && outcome.count > 0 && (
        <>
          <ListHeader>
            <span>{t('settings.automation.accountingRules.form.previewListHeader')}</span>
            <Badge type="default">{outcome.count}</Badge>
          </ListHeader>
          <TransactionList ref={scrollRef}>
            {renderItems((transaction, style, index) => (
              <TransactionRow
                key={transaction.id}
                as={transaction.document_id ? 'a' : 'div'}
                href={
                  transaction.document_id
                    ? getDocumentsPageUrl(slug, { id: transaction.document_id })
                    : undefined
                }
                target={transaction.document_id ? '_blank' : undefined}
                rel={transaction.document_id ? 'noreferrer' : undefined}
                style={style}
                $last={index === results.length - 1}
              >
                <Line>
                  <Source>
                    {sourceIcon(transaction.data_source)}
                    <SourceLabel>
                      {t(
                        'document.importSource.hoverTitle.' +
                          getImportSourceLabelKey(
                            (transaction.data_source as ImportDataSource) ??
                              ImportDataSource.BANK_INTEGRATION
                          )
                      )}
                    </SourceLabel>
                  </Source>
                  <DateText>{formatDate(transaction.date)}</DateText>
                  {transaction.document_id && (
                    <OpenIcon aria-hidden>
                      <FaExternalLinkAlt />
                    </OpenIcon>
                  )}
                </Line>
                <Line>
                  <Contact>{transaction.contact_hint}</Contact>
                  {transaction.amount == null ? (
                    <MissingAmount>—</MissingAmount>
                  ) : (
                    <MonetaryAmount value={transaction.amount} showColor />
                  )}
                </Line>
                <Line>
                  <Description>{transaction.description}</Description>
                  {transaction.reference_number && (
                    <Reference>
                      {t('settings.automation.accountingRules.form.previewReference', {
                        reference: transaction.reference_number
                      })}
                    </Reference>
                  )}
                </Line>
              </TransactionRow>
            ))}
          </TransactionList>
          <ListNote>
            {t(
              isPurchaseInvoiceRule
                ? 'settings.automation.accountingRules.form.previewListNotePurchase'
                : 'settings.automation.accountingRules.form.previewListNote'
            )}
          </ListNote>
        </>
      )}
    </StyledPreviewResults>
  )
}

export default AccountingRulePreviewResults

const StyledPreviewResults = styled.div<{ $hasList: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  min-height: ${({ $hasList }) => ($hasList ? 'auto' : '480px')};
  justify-content: ${({ $hasList }) => ($hasList ? 'flex-start' : 'center')};
  padding: ${({ theme, $hasList }) =>
    $hasList
      ? `${theme.spacing.xl}rem ${theme.spacing.lg}rem ${theme.spacing.lg}rem`
      : `${theme.spacing.xxl}rem ${theme.spacing.xl}rem`};
`

const variantColor = (theme: DefaultTheme, variant: PreviewOutcome['kind']): string =>
  ({
    loading: theme.colors.metalGray,
    matches: theme.colors.success,
    failed: theme.colors.warning
  }[variant])

const StyledPreviewIcon = styled.div<{ $variant: PreviewOutcome['kind'] }>`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${({ theme, $variant }) => `${variantColor(theme, $variant)}20`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: ${({ theme, $variant }) => variantColor(theme, $variant)};
`

const StyledPreviewTitle = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
`

const StyledPreviewDescription = styled.p`
  margin: 0;
  max-width: 440px;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const TransactionList = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  overflow: auto;
  max-height: 560px;
  text-align: left;
`

const TransactionRow = styled.a<{ $last: boolean }>`
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border-bottom: 2px solid
    ${({ theme, $last }) => ($last ? 'transparent' : theme.colors.borderSoft)};
  background: ${({ theme }) => theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: inherit;
  text-decoration: none;
  cursor: pointer;

  &,
  &:hover,
  &:focus,
  &:visited,
  & * {
    text-decoration: none !important;
    cursor: pointer;
  }

  &:hover {
    border-bottom-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.neutralWhite};
  }
`

const OpenIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.iconGray};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;

  ${TransactionRow}:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;

  & + & {
    margin-top: 4px;
  }
`

const Source = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;

  svg {
    font-size: ${({ theme }) => theme.fontSize.xs}rem;
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const SourceLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

const DateText = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const MissingAmount = styled.span`
  color: ${({ theme }) => theme.colors.metalGray};
`

const Contact = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Description = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const Reference = styled.span`
  margin-left: auto;
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-variant-numeric: tabular-nums;
`
const ListNote = styled.p`
  margin: 0;
  width: 100%;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`
